import { useCallback, useMemo } from "react";
import { api } from "../../api/client.ts";
import { countryOption } from "../../components/CountryName.tsx";
import type { FilterOption, FilterSpec } from "../../components/filters/specs.ts";
import { ORG_FORM_NAMES } from "../../data/orgForms.ts";
import { useFilterOptions } from "../../hooks/useFilterOptions.ts";
import { formatBucketLabel } from "./utils.ts";

const toOptions = (names: string[]): FilterOption[] => names.map((name) => ({ value: name, label: name }));

/**
 * The Companies page's filters. URL params are the page's bookmark format and
 * don't change; `companyQueryFilters` maps them to the API.
 */
export function useCompanyFilterSpecs(currency: string): { specs: FilterSpec[]; contactRoleOptions: FilterOption[] } {
  const { data: options } = useFilterOptions();
  const loadIndustries = useCallback(async (q: string) => toOptions(await api.searchAiIndustryOptions(q)), []);
  const loadVerticals = useCallback(async (q: string) => toOptions(await api.searchAiServedVerticalOptions(q)), []);
  const loadTech = useCallback(async (q: string) => toOptions(await api.searchTechnologyOptions(q)), []);

  const contactRoleOptions = useMemo(
    () => (options?.contact_role_types ?? []).map((rt) => ({ value: rt.code, label: rt.label })),
    [options?.contact_role_types],
  );

  const specs = useMemo<FilterSpec[]>(() => {
    const loading = !options;
    return [
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        pinned: true,
        loading,
        options: (options?.countries ?? []).map((code) => {
          const o = countryOption(code);
          return { value: o.code, label: o.label };
        }),
        searchPlaceholder: "Search countries",
      },
      {
        kind: "options",
        key: "industry",
        label: "Industry",
        param: "industry_keywords",
        pinned: true,
        loadOptions: loadIndustries,
        freeSolo: true,
        searchPlaceholder: "Search or type an industry",
      },
      {
        kind: "options",
        key: "employees",
        label: "Employees",
        param: "emp_bucket",
        pinned: true,
        loading,
        options: (options?.employee_size_buckets ?? []).map((b) => ({ value: b, label: formatBucketLabel(b) })),
      },
      {
        kind: "range",
        key: "revenue",
        label: "Revenue",
        group: "Financials",
        minParam: "rev_min",
        maxParam: "rev_max",
        unit: currency,
      },
      {
        kind: "range",
        key: "op_result",
        label: "Operating result",
        group: "Financials",
        minParam: "opr_min",
        maxParam: "opr_max",
        unit: currency,
      },
      {
        kind: "options",
        key: "org_form",
        label: "Org form",
        group: "Company",
        param: "org_form",
        loading,
        options: (options?.org_forms ?? []).map((code) => ({ value: code, label: ORG_FORM_NAMES[code] ?? code, hint: code })),
      },
      {
        kind: "options",
        key: "served_vertical",
        label: "Serves vertical",
        group: "Company",
        param: "served_vertical_keywords",
        loadOptions: loadVerticals,
        freeSolo: true,
        searchPlaceholder: "Search or type a vertical",
      },
      {
        kind: "options",
        key: "tech",
        label: "Technology",
        group: "Company",
        param: "tech",
        loadOptions: loadTech,
        searchPlaceholder: "Search technologies",
      },
      {
        kind: "options",
        key: "contact_role",
        label: "Contact role",
        group: "People",
        param: "contact_role",
        loading,
        options: contactRoleOptions,
      },
      { kind: "text", key: "contact_name", label: "Contact name", group: "People", param: "contact_name" },
      {
        kind: "options",
        key: "job_kw",
        label: "Job ad keywords",
        group: "Activity",
        param: "job_kw",
        options: [],
        freeSolo: true,
        searchPlaceholder: "Type a keyword and press Enter",
      },
      { kind: "text", key: "reg_kw", label: "Regulatory filing", group: "Activity", param: "reg_kw", placeholder: "Filing mentions…" },
      {
        kind: "options",
        key: "reg_status",
        label: "Filing status",
        group: "Activity",
        param: "reg_status",
        multiple: false,
        options: [
          { value: "active", label: "Active" },
          { value: "completed", label: "Completed" },
        ],
      },
    ];
  }, [options, currency, contactRoleOptions, loadIndustries, loadVerticals, loadTech]);

  return { specs, contactRoleOptions };
}
